import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Search, X, Users, FileStack, MapPin, FileText, Receipt, Truck, Package, UserPlus, Loader2, Command as CmdIcon } from "lucide-react";

const ICON = {
  clientes: Users,
  peticiones: FileStack,
  espacios: MapPin,
  presupuestos: FileText,
  gastos: Receipt,
  vehiculos: Truck,
  componentes: Package,
  leads: UserPlus,
};
const LABEL = {
  clientes: "Clientes",
  peticiones: "Peticiones",
  espacios: "Espacios",
  presupuestos: "Presupuestos",
  gastos: "Gastos",
  vehiculos: "Vehículos",
  componentes: "Componentes",
  leads: "Leads",
};

/** Paleta de comandos / búsqueda global. Abre con Cmd/Ctrl + K. */
export default function GlobalSearch({ navItems = [] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();
  const abortRef = useRef(null);

  // Atajo Cmd/Ctrl + K + evento custom 'dnf:open-search'
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    const onCustomOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("dnf:open-search", onCustomOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("dnf:open-search", onCustomOpen);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setResults(null); }
  }, [open]);

  // Debounce de búsqueda
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    if (abortRef.current) clearTimeout(abortRef.current);
    abortRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q, limit: 6 } });
        setResults(data);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => abortRef.current && clearTimeout(abortRef.current);
  }, [q, open]);

  const goTo = useCallback((path) => { setOpen(false); nav(path); }, [nav]);

  // Filtrado local de "Páginas" (items del sidebar) — inmediato
  const navMatches = useMemo(() => {
    if (!q.trim() || q.trim().length < 2) return [];
    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const nq = norm(q.trim());
    return navItems
      .map((it) => {
        const nl = norm(it.label);
        const ng = norm(it.group || "");
        const na = norm((it.alias || []).join(" "));
        let score = 0;
        if (nl.startsWith(nq)) score += 100;
        else if (nl.includes(nq)) score += 50;
        else if (na.includes(nq)) score += 40;
        if (ng.includes(nq)) score += 10;
        return { it, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map((x) => x.it);
  }, [q, navItems]);

  if (!open) {
    // Floating button bottom-right (solo en desktop, mobile usa el de menú)
    // Icon-only para no competir con "Pregunta IA" y "Made with Emergent"
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="open-search"
        title="Buscar (⌘K)"
        aria-label="Buscar"
        className="fixed bottom-40 right-5 z-40 hidden md:flex items-center justify-center w-11 h-11 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 rounded-full shadow-xl hover:shadow-2xl hover:scale-110 transition-all group">
        <Search className="w-4 h-4" />
        <span className="absolute right-full mr-2 px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-[10px] text-zinc-300 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
          Buscar <kbd className="ml-1 text-[9px] bg-zinc-800 px-1 py-0.5 rounded border border-zinc-700">⌘K</kbd>
        </span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] bg-black/70 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-testid="search-overlay">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl mx-4 bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800">
          <Search className="w-4 h-4 text-zinc-500" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar clientes, peticiones, espacios, presupuestos, gastos..."
            data-testid="search-input"
            className="flex-1 bg-transparent text-zinc-100 placeholder:text-zinc-500 outline-none text-sm"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />}
          <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-100" data-testid="search-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {!q && (
            <div className="px-4 py-8 text-center text-zinc-500 text-sm">
              <CmdIcon className="w-6 h-6 mx-auto mb-2 opacity-40" />
              Escribe para buscar en toda la app.
              <div className="mt-3 text-xs">
                Atajos: <kbd className="px-1.5 py-0.5 bg-zinc-900 rounded text-[10px] border border-zinc-800">⌘K</kbd> abrir / cerrar ·
                <kbd className="px-1.5 py-0.5 bg-zinc-900 rounded text-[10px] border border-zinc-800 ml-1">Esc</kbd> cerrar
              </div>
            </div>
          )}
          {q && results && results.total === 0 && navMatches.length === 0 && !loading && (
            <div className="px-4 py-8 text-center text-zinc-500 text-sm">
              No hay resultados para <b className="text-zinc-300">"{q}"</b>.
              <div className="mt-2 text-xs">Prueba con el botón <span className="text-violet-300">✨ Pregúntale a la IA</span> abajo.</div>
            </div>
          )}
          {/* PÁGINAS (sidebar) — solo coincidencia local, sin red */}
          {navMatches.length > 0 && (
            <div className="px-2 py-2">
              <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                <CmdIcon className="w-3 h-3" /> Páginas · {navMatches.length}
              </div>
              {navMatches.map((it) => {
                const NavIcon = it.icon || Search;
                return (
                  <button
                    key={`nav-${it.to}`}
                    onClick={() => goTo(it.to)}
                    data-testid={`result-pagina-${it.to.replace(/\//g, "-")}`}
                    className="w-full text-left px-3 py-2 rounded hover:bg-zinc-900 transition-colors flex items-center gap-3 group"
                  >
                    <NavIcon className="w-4 h-4 text-amber-400/70 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-100 truncate group-hover:text-amber-200">{it.label}</div>
                      {it.group && <div className="text-[10px] text-zinc-500">{it.group}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {results && Object.entries(results.results).map(([key, items]) => {
            if (!items || items.length === 0) return null;
            const Icon = ICON[key] || Search;
            return (
              <div key={key} className="px-2 py-2">
                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                  <Icon className="w-3 h-3" /> {LABEL[key]} · {items.length}
                </div>
                {items.map((it) => (
                  <SearchRow key={`${key}-${it.id}`} item={it} type={key} onClick={goTo} />
                ))}
              </div>
            );
          })}
        </div>

        <div className="border-t border-zinc-800 px-4 py-2.5 bg-zinc-950/60 flex items-center justify-between text-[10px] text-zinc-500">
          <span>{results ? `${results.total} resultados` : "—"}</span>
          <span>⌘K para abrir · Esc para cerrar</span>
        </div>
      </div>
    </div>
  );
}

function SearchRow({ item, type, onClick }) {
  const text = labelFor(item, type);
  const subText = subFor(item, type);
  const path = pathFor(item, type);
  return (
    <button
      onClick={() => path && onClick(path)}
      data-testid={`result-${type}-${item.id}`}
      disabled={!path}
      className="w-full text-left px-3 py-2 rounded hover:bg-zinc-900 transition-colors flex items-start gap-3 group"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 truncate group-hover:text-amber-200">{text}</div>
        {subText && <div className="text-[11px] text-zinc-500 truncate">{subText}</div>}
      </div>
    </button>
  );
}

function labelFor(it, type) {
  switch (type) {
    case "clientes": return it.nombre || "—";
    case "peticiones": return `${it.codigo || "—"} · ${it.nombre || ""}`;
    case "espacios": return it.nombre || "—";
    case "presupuestos": return `${it.numero || "—"} · ${(it.cliente_snapshot?.razon_social) || ""}`;
    case "gastos": return it.descripcion || it.concepto || "Gasto";
    case "vehiculos": return `${it.matricula || ""} ${it.marca || ""} ${it.modelo || ""}`.trim();
    case "componentes": return it.descripcion || "—";
    case "leads": return `${it.nombre || ""} ${it.empresa ? `· ${it.empresa}` : ""}`;
    default: return JSON.stringify(it).slice(0, 60);
  }
}
function subFor(it, type) {
  switch (type) {
    case "clientes": return [it.cif, it.direccion_fiscal, it.email_contacto].filter(Boolean).join(" · ");
    case "peticiones": return [it.cliente_nombre, it.estado, (it.fecha_creacion || "").slice(0, 10)].filter(Boolean).join(" · ");
    case "espacios": return [it.cliente_nombre, it.direccion].filter(Boolean).join(" · ");
    case "presupuestos": return [it.estado, `${it.total || 0}€`, (it.fecha_creacion || "").slice(0, 10)].filter(Boolean).join(" · ");
    case "gastos": return [it.categoria, `${it.importe || 0}€`, (it.fecha || "").slice(0, 10)].filter(Boolean).join(" · ");
    case "vehiculos": return [it.alias, it.activo ? "Activo" : "Inactivo"].filter(Boolean).join(" · ");
    case "componentes": return `${it.cantidad} ud · ${it.precio_unitario || 0}€ · ${it.estado || "—"}`;
    case "leads": return [it.email, it.estado].filter(Boolean).join(" · ");
    default: return "";
  }
}
function pathFor(it, type) {
  switch (type) {
    case "clientes": return `/clientes`;
    case "peticiones": return `/peticiones/${it.id}`;
    case "espacios": return `/espacios`;
    case "presupuestos": return `/presupuestos/${it.id}`;
    case "gastos": return `/gastos`;
    case "vehiculos": return `/vehiculos`;
    case "componentes": return it.peticion_id ? `/peticiones/${it.peticion_id}` : null;
    case "leads": return `/leads`;
    default: return null;
  }
}
